import {noop} from '@benchling/js-utils';
import classNames from 'classnames';
// @ts-expect-error
import onecolor from 'onecolor';
import React, {CSSProperties, ReactNode} from 'react';
import isEqual from 'react-fast-compare';

import * as contextmenu from 'components/ContextMenu';
import {SEARCH_RESULT_COLOR, SELECTED_SEARCH_RESULT_COLOR} from 'notebook/mediocre/search/SearchConstants';
import {
  getSearchResultsAtRoot,
  getSelectedSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import SelectorDropdown from 'notebook/plain-tables/selectors/SelectorDropdown';
import getFormattedCellValue, {
  CellStateInfo,
  isInteractiveFormattedValue,
} from 'notebook/structured-tables/columns/FormattedCellValue';
import {LastSyncedUpstreamValue} from 'notebook/structured-tables/StructuredTableConstants';
import {
  TABLE_CELL_BG_IMPORTANT_COLOR,
  TABLE_CELL_TOP_PADDING,
} from 'notebook/tables/constants/TableLayoutConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {TableCellAffixedProps} from 'notebook/tables/core-ui/TableCellRenderUtils';
import {TableEditableClass, TablePasteOptions} from 'notebook/tables/core-ui/TableEditable';
import ErrorTriangle, {TriangleLocation} from 'notebook/tables/errors/ErrorTriangle';
import IllegalErrorTriangle from 'notebook/tables/errors/IllegalErrorTriangle';
import {TriangleState} from 'notebook/tables/errors/TableErrorConstants';
import excess from 'notebook/tables/excess';
import {EvaluatedCellError, Selector} from 'notebook/tables/excess/Evaluator';
import {hasAnyBlockingErrors} from 'notebook/tables/excess/Util';
import {useCellModel} from 'notebook/tables/store/hooks/useCellModel';
import TableUtil from 'notebook/tables/TableUtil';
import {isNumeric, isString} from 'notebook/tables/typed-values/DataTypes';
import {errorIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import {checkClipboardReadAccess, checkClipboardWriteAccess} from 'util/clipboard/ClipboardUtil';
import {ClipboardItemType} from 'util/clipboard/ClipboardUtil';
import * as keyboard from 'util/Keyboard';
import * as logging from 'util/Logging';
import {ElementClipboardEvent, ElementMouseEvent, Optional} from 'util/Types';

import {isRowArchived} from '../util/isRowArchived';

type CellIndex = [number, number]; // [i, j]

const SELECTED_BACKGROUND_COLOR = onecolor(BLUE_LIGHT);

const NO_CLIPBOARD_DATA_WARNING = 'No data found on clipboard. Try copying the data again before retrying.';

/**
 * Returns a onecolor Color which is the result of mixing `colorA`
 * and `colorB` in the proportion of `ratio`.
 */
function _blend(colorA: typeof onecolor, colorB: typeof onecolor, ratio?: number): typeof onecolor {
  if (ratio == null) {
    ratio = 0.5;
  }
  const red = colorA.red() + (colorB.red() - colorA.red()) * ratio;
  const green = colorA.green() + (colorB.green() - colorA.green()) * ratio;
  const blue = colorA.blue() + (colorB.blue() - colorA.blue()) * ratio;
  return new onecolor.RGB(red, green, blue);
}

export interface TableCellProps extends MediocreSearchResultProps, TableCellAffixedProps {
  fileId: string;
  tableNamespace: string;
  locked: boolean;
  getLastIndex: () => TableIndex;
  cid: string;
  i: number;
  j: number;
  isHeaderCell: boolean;
  isFirstColumn?: boolean;
  headerRowHeight?: number;
  isSingleInputCell?: boolean;
  // These must be used instead of cell.mergeRowSpan/mergeColSpan or evaluatedCell.mergeBox
  rowSpan?: number;
  colSpan?: number;
  isHiddenMergedCell?: boolean;

  // Only available if the cell is part of a StructuredTable and was previously submitted or refreshed
  lastSyncedUpstreamValue?: LastSyncedUpstreamValue;

  getCopySelection: TableEditableClass['_getCopySelection'];
  getCopySelectionHTML: TableEditableClass['_getCopySelectionHTML'];
  clearSelection: TableEditableClass['deleteRangeHelper'];
  multiPasteHandler: TableEditableClass['multiPasteHandler'];
  hasLockedCellInSelection: TableEditableClass['hasLockedCellInSelection'];

  // "Stateful" props
  hasActiveEdit: boolean; // If this cell is actively being edited
  highlightClass?: string;
  isSelected: boolean;
  /**
   * The double selected cell is the cell that is the focal cell of a selection.
   * For example, if the user clicks on the cell and drags down, the cell is double selected, but if the user
   * clicks on a different cell and then drags down to include the cell, the cell is not double selected.
   */
  isDoubleSelected: boolean;

  isFillDownBoxLeftEdge: boolean;
  isFillDownBoxRightEdge: boolean;
  isFillDownBoxTopEdge: boolean;
  isFillDownBoxBottomEdge: boolean;

  // Actions
  storeState: TableEditableClass['saveMultiSelect'];
  commitCell: TableEditableClass['updateCell'];
  selectCell: TableEditableClass['selectCell'];

  // Event handlers
  cellDragOver: TableEditableClass['cellDragOverHandler'];
  cellDragStart: TableEditableClass['multiEditableMouseDownHandler'];
  cellMouseMoveHandler: TableEditableClass['fillDownMouseMoveHandler'];
  // Set if cell should show the fill down square
  fillDownSquareMouseDownHandler: TableEditableClass['fillDownSquareMouseDownHandler'] | null;
  fillDownSquareDoubleClickHandler: TableEditableClass['fillDownSquareDoubleClickHandler'] | null;

  // Selectors
  selectorsEnabled: boolean;
  openManageSelectorsModal: (initialSelectorId: string) => void;

  // Has the row been in the view port of the browser yet?
  hasRowBeenInView?: boolean;
}

export function TableCell({
  fileId,
  cid,
  i,
  j,
  lastSyncedUpstreamValue,
  selectedSearchResultTree,
  searchResultsTree,
  isHeaderCell,
  locked,
  isDoubleSelected,
  isSelected,
  selectorsEnabled,
  hasRowBeenInView,
  isFillDownBoxLeftEdge,
  isFillDownBoxRightEdge,
  isFillDownBoxTopEdge,
  isFillDownBoxBottomEdge,
  isAffixed,
  affixedLeftSpacing,
  isFirstColumn,
  isSingleInputCell,
  isHiddenMergedCell,
  showAffixedShadow,
  isLastFrozenColumn,
  highlightClass,
  colSpan,
  rowSpan,
  headerRowHeight,
  tableNamespace,
  cellMouseMoveHandler,
  cellDragOver,
  storeState,
  commitCell,
  selectCell,
  cellDragStart,
  fillDownSquareMouseDownHandler,
  fillDownSquareDoubleClickHandler,
  getCopySelection,
  getCopySelectionHTML,
  hasLockedCellInSelection,
  multiPasteHandler,
  clearSelection,
  getLastIndex,
  openManageSelectorsModal,
  hasActiveEdit,
}: TableCellProps): JSX.Element {
  const {showFormulas, isExportView} = EditorPropertiesContext.useContext();

  const {cell, evaluatedCell, columnConstraint = null, rowData = null, error, errorState} = useCellModel(j);
  const {
    bold,
    underline,
    italic,
    strikethrough,
    text,
    grayOutContents,
    locked: cellLocked,
    foregroundColor,
    backgroundColor,
    important,
    userAnnotationIndicator,
    formattedValue,
  } = cell;
  const {value, selector, locked: evaluatedCellLocked} = evaluatedCell;

  const index: CellIndex = [i, j];

  /**
   * Note: This should not be used for any special locking behavior, e.g. where we intend to render a
   * cell as partially locked, like if the entry is read-only but the cell would otherwise be writable.
   */
  const isLocked = Boolean(locked || evaluatedCellLocked || cellLocked || columnConstraint?.isProtected);
  const isLastRow = i === getLastIndex()[0];
  const isLastColumn = j === getLastIndex()[1];

  /**
   * Returns the background color of a cell when without interaction. Takes into account
   * the color derived from the columnConstraint, the explicit color set on the cell, and
   * the header cell.
   *
   * Note: Column constraint color has less importance than explicit color or header cell color.
   */
  const getUnselectedBackgroundColor = (): Optional<string> => {
    const {results, selected} = getSearchResultsAtRoot({
      selectedSearchResultTree,
      searchResultsTree,
    });
    if (selected) {
      return SELECTED_SEARCH_RESULT_COLOR;
    } else if (results.length > 0) {
      return SEARCH_RESULT_COLOR;
    }
    if (important) {
      // Is important color overrides explicit background color
      return TABLE_CELL_BG_IMPORTANT_COLOR;
    }
    if (evaluatedCellLocked) {
      // We choose not to use `isLocked` here for the case of a locked entry. `isLocked` tells
      // us whether a cell should be fully locked, disregarding whether it was originally writeable or not,
      // but we want to render background color based on whether the cell was evaluated to be locked.
      return GRAY_LIGHTER;
    }
    if (backgroundColor != null || isHeaderCell) {
      // Explicit color wins over color derived from constraint.
      return backgroundColor;
    }
    return null;
  };

  /**
   * Return the cell's current background color as a String, or null if the
   * color shouldn't be set.
   */
  const getBackgroundColor = (): string | undefined => {
    const unselectedColor = getUnselectedBackgroundColor();
    if (!unselectedColor || isDoubleSelected || highlightClass) {
      return;
    }
    if (!isSelected) {
      return unselectedColor;
    }
    const backgroundOneColor = onecolor(unselectedColor);
    if (!backgroundOneColor) {
      logging.warn(`Invalid color: ${unselectedColor}`);
      return;
    }
    return _blend(backgroundOneColor, SELECTED_BACKGROUND_COLOR).hex().toUpperCase();
  };

  /**
   * Classes are preferred over inline styles, but inlining is necessary
   * for parameterized styles, e.g. color.
   */
  const dynamicStyles: React.CSSProperties = {
    color: foregroundColor ?? undefined,
    backgroundColor: getBackgroundColor(),
    left: affixedLeftSpacing ? `${affixedLeftSpacing}px` : undefined,
  };

  const handleCellMouseMove = (): void => {
    cellMouseMoveHandler(index);
  };

  const handleCellMouseOver = (): void => {
    cellDragOver(index);
  };

  const handleSelectorDropdownUpdate = (text: string): void => {
    storeState();
    commitCell(index, text);
    selectCell(index, {
      text,
      editing: false,
      collapse: true,
    });
  };

  const handleCellDoubleClick = (): void => {
    if (isLocked) {
      return;
    }
    // If the cursor is next to a cell range, clicking another cell will replace the cell ref.
    // If we were to move the cursor to the end of the formula on double click, this could
    //   result in a sticky situation if the formula ends in a cell range, e.g. "=A1 + B2".
    // The user might want to dblclick to inspect the formula, and then simply click out of the
    //   cell, but they will trigger the above described behaviour.
    // To avoid this, we simply highlight the entire cell contents (which is a reasonable
    //   alternative behaviour that is stateless :)
    selectCell(index, {
      editing: true,
      collapse: true,
      selection: {cid, start: 0, end: text.length},
    });
  };

  const handleCellMouseDown = (e: ElementMouseEvent): void => {
    cellDragStart({start: index, active: 'both'}, e);
  };

  const handleFillDownSquareMouseDown = (e: ElementMouseEvent): void => {
    e.stopPropagation();
    fillDownSquareMouseDownHandler?.(e);
  };

  const handleFillDownSquareDoubleClick = (e: ElementMouseEvent): void => {
    e.stopPropagation();
    fillDownSquareDoubleClickHandler?.(e);
  };

  const handleSelectorDropdownOpen = (): void => {
    selectCell(index, {editing: false, collapse: true});
  };

  const getRenderedCellContent = ({
    showFormulas,
    isExportView,
  }: {
    showFormulas: boolean;
    isExportView?: boolean;
  }): {renderedCellContent: ReactNode; renderedSelector?: ReactNode} => {
    // By default, the cell is rendered based on its content, but the parent component can control
    // what is rendered by passing a custom component in formattedValue.
    if (formattedValue != null) {
      return {renderedCellContent: formattedValue};
    }
    if (TableUtil.isCellFormula(cell) && showFormulas) {
      return {renderedCellContent: text};
    }

    const showSelector =
      selectorsEnabled &&
      selector != null &&
      selector !== excess.util.MISSING_SELECTOR &&
      (selector as Selector).options != null;

    let renderedSelector = showSelector ? (
      <SelectorDropdown
        isSelected={isDoubleSelected}
        locked={isLocked}
        selector={selector as Selector}
        onOpenManageSelectorsModal={openManageSelectorsModal}
        onOpenDropdown={handleSelectorDropdownOpen}
        onUpdate={handleSelectorDropdownUpdate}
      />
    ) : undefined;

    const cellStateInfo: CellStateInfo = {
      locked: isLocked,
      cellLocked: isLocked,
      isDoubleSelected,
      isSelected,
      commitCell: (typedValue) => {
        commitCell([i, j], '', {typedValue});
      },
      setCustomSelectorElement: (selector: JSX.Element) => {
        renderedSelector = selector;
      },
      handleSelectCell: handleSelectorDropdownOpen,
      handleDoubleClick: handleCellDoubleClick,
      cellSelectorHandler: handleSelectorDropdownUpdate,
      lastSyncedUpstreamValue,
      hasRowBeenInView,
      isExportView,
    };

    const renderedCellContent = getFormattedCellValue(
      columnConstraint,
      cell,
      value,
      cellStateInfo,
      rowData?.rowMetadata ?? null,
      fileId
    );

    return {
      renderedCellContent,
      renderedSelector,
    };
  };

  const hasInteractiveFormattedValue =
    formattedValue != null ? true : isInteractiveFormattedValue(columnConstraint, cell, value);

  const shouldRenderErrorTriangle = isRowArchived(rowData) ? false : error != null;
  const userAnnotationIndicatorWillRender = !error && Boolean(userAnnotationIndicator);
  const shouldShowResolutionButton = !isLocked && !hasAnyBlockingErrors(error);

  const renderCellIndicator = (cellError: EvaluatedCellError | undefined | null): React.ReactNode => {
    if (!shouldRenderErrorTriangle || cellError == null) {
      return userAnnotationIndicator;
    }
    if (errorState === TriangleState.ILLEGAL) {
      return <IllegalErrorTriangle error={cellError} location={TriangleLocation.CELL} />;
    }
    return (
      <ErrorTriangle
        location={TriangleLocation.CELL}
        error={cellError}
        showErrorResolutionButtons={shouldShowResolutionButton}
        state={errorState}
        dataTestAttrs={{component: 'TableCell', element: 'dogEar', key: errorState}}
      />
    );
  };

  /**
   * Returns true if successfully able to write data to clipboard, and false otherwise.
   */
  const copySelectionToClipboard = async ({
    failureMessage,
    onSuccess,
  }: {
    failureMessage: string;
    onSuccess?: () => void;
  }): Promise<void> => {
    const allowed = await checkClipboardWriteAccess();
    if (!allowed) {
      return;
    }
    const blobText = Promise.resolve(new Blob([getCopySelection()], {type: ClipboardItemType.TEXT}));
    const blobHTML = Promise.resolve(new Blob([getCopySelectionHTML()], {type: ClipboardItemType.HTML}));
    try {
      await navigator.clipboard.write([
        // Storing html of a table item which contains all the information in a cell (equations, links, etc)
        // also storing plain text which caries just the plain text in a cell
        new ClipboardItem({
          [ClipboardItemType.TEXT]: await blobText,
          [ClipboardItemType.HTML]: await blobHTML,
        }),
      ]);
      onSuccess?.();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch {
      notifications.warn(failureMessage);
    }
  };

  const getContextMenuOptions = (): Array<contextmenu.ContextMenuOption> => {
    const options: Array<contextmenu.ContextMenuOption> = [];
    // As of 2023-12-01, Firefox does not support read or write in non-extension code,
    // see: https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/read
    // The Typescript compiler doesn't recognize that navigator.clipboard.write/read could be undefined,
    // so we cast it to Optional to avoid the error
    if (navigator.clipboard.write as Optional<typeof navigator.clipboard.write>) {
      options.push(
        {
          name: 'Cut',
          shortcut: `${keyboard.ctrl} + X`,
          click: handleCut,
          disabled: locked || hasLockedCellInSelection(),
        },
        {
          name: 'Copy',
          shortcut: `${keyboard.ctrl} + C`,
          click: handleCopy,
          disabled: false,
        }
      );
    }
    if (navigator.clipboard.read as Optional<typeof navigator.clipboard.read>) {
      options.push(
        {
          name: 'Paste',
          shortcut: `${keyboard.ctrl} + V`,
          click: handleNormalPaste,
          disabled: locked,
        },
        {
          name: 'Paste Special',
          menu: [
            {
              name: 'Paste into one cell',
              click: handlePasteIntoOneCell,
              disabled: locked,
            },
            {
              name: 'Paste down rows',
              click: handlePasteDownRows,
              disabled: locked,
            },
            {
              name: 'Paste transpose',
              click: handlePasteTranspose,
              disabled: locked,
            },
            {
              name: 'Paste as text',
              click: handlePasteAsText,
              disabled: locked,
            },
          ],
          disabled: locked,
        }
      );
    }

    return options;
  };

  /**
   * Reads data from clipboard and returns parsed comma separated strings in preparation for pasting
   * into one cell.
   */
  const readClipboardContentAsCSVString = async (): Promise<string> => {
    await checkClipboardReadAccess();
    let text;
    try {
      text = await navigator.clipboard.readText();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.warn(`Unable to read data from clipboard: ${e}`);
      return '';
    }
    const csv = text
      .split(/[\t\n]/gm)
      .map((cellText) => cellText.trim())
      .filter((cellText) => cellText)
      .join(', ');
    if (!csv) {
      notifications.warn(NO_CLIPBOARD_DATA_WARNING);
    }
    return csv;
  };

  const readClipboardContentAsMultilineString = async (): Promise<string> => {
    await checkClipboardReadAccess();
    let text;
    try {
      text = await navigator.clipboard.readText();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.warn(`Unable to read data from clipboard: ${e}`);
      return '';
    }
    const delimiter = text.includes('\t') ? '\t' : ',';
    const cellValues = text
      .split(delimiter)
      .map((cellText) => cellText.trim())
      .filter((cellText) => cellText)
      .join('\n');
    if (cellValues.length === 0) {
      notifications.warn(NO_CLIPBOARD_DATA_WARNING);
    }
    return cellValues;
  };

  const readClipboardContentAsDataTransfer = async (): Promise<DataTransfer> => {
    await checkClipboardReadAccess();
    const clipboardData = new window.DataTransfer();
    try {
      const clipboardItems = await navigator.clipboard.read();
      if (clipboardItems.length === 0) {
        throw new Error('Clipboard is empty');
      }
      const clipboardItem = clipboardItems[0];

      if (clipboardItem.types.length === 0) {
        clipboardData.setData(ClipboardItemType.TEXT, '');
        return clipboardData;
      }
      for (const type of clipboardItem.types) {
        const blob = await clipboardItem.getType(type);
        const data = await blob.text();
        clipboardData.setData(type, data);
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.warn(`Unable to read data from clipboard: ${e}`);
      clipboardData.setData(ClipboardItemType.TEXT, '');
    }
    return clipboardData;
  };

  const dispatchPasteHandler = (
    clipboardData: DataTransfer,
    options: TablePasteOptions = {type: ClipboardItemType.TEXT}
  ): void => {
    multiPasteHandler(
      {
        clipboardData,
        preventDefault: noop,
      } as ElementClipboardEvent,
      {...options, selectionIndex: options.selectionIndex ?? index}
    );
  };

  const handleCut = async (): Promise<void> => {
    await copySelectionToClipboard({
      failureMessage: 'Unable to cut to clipboard.',
      onSuccess: clearSelection,
    });
  };

  const handleCopy = async (): Promise<void> => {
    await copySelectionToClipboard({
      failureMessage: 'Unable to copy to clipboard.',
    });
  };

  const handleNormalPaste = async (): Promise<void> => {
    const clipboardData = await readClipboardContentAsDataTransfer();
    dispatchPasteHandler(clipboardData);
  };

  const handlePasteIntoOneCell = async (): Promise<void> => {
    const csv = await readClipboardContentAsCSVString();
    if (!csv) {
      return;
    }
    const clipboardData = new window.DataTransfer();
    clipboardData.setData(ClipboardItemType.TEXT, csv);
    dispatchPasteHandler(clipboardData);
  };

  const handlePasteDownRows = async (): Promise<void> => {
    const cellValues = await readClipboardContentAsMultilineString();
    if (!cellValues) {
      return;
    }
    const clipboardData = new window.DataTransfer();
    clipboardData.setData(ClipboardItemType.TEXT, cellValues);
    dispatchPasteHandler(clipboardData);
  };

  const handlePasteTranspose = async (): Promise<void> => {
    const clipboardData = await readClipboardContentAsDataTransfer();
    const type = clipboardData.types.includes(ClipboardItemType.HTML)
      ? ClipboardItemType.HTML
      : ClipboardItemType.TEXT;
    dispatchPasteHandler(clipboardData, {transpose: true, type});
  };

  const handlePasteAsText = async (): Promise<void> => {
    // Get text data from clipboard
    const clipboardData = await readClipboardContentAsDataTransfer();
    if (!clipboardData.types.includes(ClipboardItemType.TEXT)) {
      notifications.warn('Unable to read text data from clipboard');
      return;
    }
    // Only use text data from clipboard
    clipboardData.clearData(ClipboardItemType.HTML);
    dispatchPasteHandler(clipboardData);
  };

  const handleOpenContextMenu = (e: ElementMouseEvent): void => {
    // Only open the context menu on cells that are selected. This prevents the context menu from opening on cells that we aren't set to interact with yet.
    if (isSelected) {
      contextmenu.view.open(e, getContextMenuOptions(), true);
    } else {
      e.preventDefault();
    }
  };

  // When rendering a merged cell, the text must be sticky to remain within the view
  const isMergedCell = Boolean(colSpan) || Boolean(rowSpan);
  const positionedContentStyle: CSSProperties = isMergedCell
    ? {
        top: (headerRowHeight ?? 0) + TABLE_CELL_TOP_PADDING,
        position: 'sticky',
        bottom: TABLE_CELL_TOP_PADDING,
        alignSelf: 'end',
      }
    : {};

  const cellTextTestAttributes = {component: 'TableCell', element: 'cellText'};

  const {renderedCellContent, renderedSelector} = getRenderedCellContent({
    showFormulas,
    isExportView,
  });

  const align = TableUtil.alignCell(cell, evaluatedCell, showFormulas, columnConstraint);

  const hasPositionedContent =
    fillDownSquareMouseDownHandler != null || // filldown square in bottom-right corner
    renderedSelector != null || // selector on middle-right
    shouldRenderErrorTriangle || // error in top-right corner
    userAnnotationIndicatorWillRender ||
    isMergedCell ||
    isDoubleSelected || // ensure that when you double click, the DOM remains the same.
    hasInteractiveFormattedValue; // if rendering a custom component, never alter the DOM

  return (
    <td
      className={classNames('cell', highlightClass, {
        'is-value': isNumeric(value),
        'is-text': isString(value),
        'has-selector': selector != null,
        'is-bold': bold != null ? bold : isHeaderCell,
        'is-mono': TableUtil.isMonospaced(columnConstraint),
        'is-underline': underline,
        'is-italic': italic,
        'is-strikethrough': strikethrough,
        'is-alignLeft': align === 'left',
        'is-alignCenter': align === 'center',
        'is-alignRight': align === 'right',
        'header-cell': !hasActiveEdit && isHeaderCell,
        'dbl-selected': isDoubleSelected,
        selected: isSelected,
        editing: hasActiveEdit,
        'is-fillDownBox-leftEdge': isFillDownBoxLeftEdge,
        'is-fillDownBox-rightEdge': isFillDownBoxRightEdge,
        'is-fillDownBox-topEdge': isFillDownBoxTopEdge,
        'is-fillDownBox-bottomEdge': isFillDownBoxBottomEdge,
        'cell-contents': !hasActiveEdit && !hasPositionedContent,
        'cell--error': error != null && !isRowArchived(rowData),
        'cell--with-validation': userAnnotationIndicatorWillRender,
        'cell-sticky': isAffixed,
        'cell-rightShadow': showAffixedShadow,
        'cell-freezeBorder': isLastFrozenColumn,
        'cell-isFirstColumn': isFirstColumn,
        'cell-isSingleInput': isSingleInputCell,
        'cell-locked': locked,
        'cell-hiddenMergedCell': isHiddenMergedCell,
      })}
      // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
      role="cell"
      aria-hidden={isHiddenMergedCell ? true : undefined}
      aria-rowindex={i}
      aria-colindex={j}
      aria-current={isSelected ? 'true' : undefined}
      onContextMenu={handleOpenContextMenu}
      colSpan={colSpan}
      rowSpan={rowSpan}
      style={dynamicStyles}
      data-cid={cid}
      data-namespace={tableNamespace}
      {...(!hasActiveEdit && {
        onMouseMove: handleCellMouseMove,
        onMouseOver: handleCellMouseOver,
        onMouseDown: handleCellMouseDown,
        onDoubleClick: handleCellDoubleClick,
        onWheel: handleCellMouseOver,
      })}
      {...getSelectedSearchResultProps({
        searchResultsTree,
        selectedSearchResultTree,
      })}
      {...getTestProps({component: 'TableCell', element: 'root'})}
    >
      {hasPositionedContent ? (
        <div className={classNames('cell-innerWrapper')}>
          {renderCellIndicator(error)}
          <div
            className={classNames('cell-contents', {
              'has-selector': renderedSelector != null,
              'should-gray-out': grayOutContents,
            })}
            style={positionedContentStyle}
            {...getTestProps(cellTextTestAttributes)}
          >
            {renderedCellContent}
          </div>
          {renderedSelector}
          {fillDownSquareMouseDownHandler != null && (
            <div
              role="button"
              aria-label="Fill cells"
              tabIndex={0}
              className={classNames('cell-fillDownSquare', {
                'cell-fillDownSquare--lastRow': isLastRow,
                'cell-fillDownSquare--lastColumn': isLastColumn,
              })}
              onMouseDown={handleFillDownSquareMouseDown}
              onDoubleClick={handleFillDownSquareDoubleClick}
              {...getTestProps({component: 'TableCell', element: 'fillDownHandle'})}
            >
              <div className="cell-fillDownSquare-fill" />
            </div>
          )}
        </div>
      ) : grayOutContents ? (
        <div
          className={classNames('cell-contents', {
            'should-gray-out': grayOutContents,
          })}
          {...getTestProps(cellTextTestAttributes)}
        >
          {renderedCellContent}
        </div>
      ) : (
        <span {...getTestProps(cellTextTestAttributes)}>{renderedCellContent}</span>
      )}
    </td>
  );
}

interface TableCellErrorBoundaryProps {
  isExportView?: boolean;
  children: JSX.Element;
}

// We intentionally keep this component very minimal to ensure it has a low chance of failure. This
// allows the overall table to still be rendered if only some of its cells are experiencing issues.
// Currently, this is only being used for exports. The errorIndicatorAttrs are used to detect the
// presence of these errors to gracefully fail and log the rendered HTML output with errors.
class TableCellErrorBoundary extends React.Component<
  TableCellErrorBoundaryProps,
  {
    hasError: boolean;
  }
> {
  constructor(props: TableCellErrorBoundaryProps) {
    super(props);
    this.state = {hasError: false};
  }

  override componentDidCatch(error: Error): void {
    // Error boundary is only used in export view, otherwise we crash the notebook view. We may revisit this
    // decision in the future, but we are concerned that allowing the notebook to render while a cell is in
    // an error state could lead to unintended side-effects such as data integrity issues if the note gets
    // saved.
    if (!this.props.isExportView) {
      throw error;
    }
    // We only explicitly set the hasError state one time. This needs to be guarded since we do not unconditionally
    // render TableCellError when an error happens. Otherwise, we will end up in a re-render loop as the cell
    // will keep erroring and updating the state here.
    if (!this.state.hasError) {
      this.setState({hasError: true});
    }
  }

  override render(): JSX.Element {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <td
        className={classNames('cell', {
          'cell--error': true,
        })}
      >
        <div className={classNames('cell-innerWrapper')} {...errorIndicatorAttrs()}>
          <IllegalErrorTriangle
            error={{type: 'Render error', message: 'Failed to render cell. Please refresh the page.'}}
            location={TriangleLocation.CELL}
          />
          Error loading cell
        </div>
      </td>
    );
  }
}

function TableCellWithErrorBoundary(props: TableCellProps): JSX.Element {
  const {isExportView} = EditorPropertiesContext.useContext();
  return (
    <TableCellErrorBoundary isExportView={isExportView}>
      <TableCell {...props} />
    </TableCellErrorBoundary>
  );
}

// TODO(BNCH-113857): After redux has been enabled, this should no longer use a deep comparison
export default React.memo(TableCellWithErrorBoundary, (prevProps, nextProps) =>
  isEqual(prevProps, nextProps)
);
